import React, { useState, useEffect } from 'react';
import { Download, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAPIClient } from '@/hooks/useAPIClient';
import { useFileMapping } from '@/hooks/useFileManagement';

interface PDFViewerProps {
  filepath: string;
  filename: string;
}

export function PDFViewer({ filepath, filename }: PDFViewerProps) {
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const apiClient = useAPIClient();

  // Fetch file mapping to find processed files for status badge
  const {
    data: mappingData,
    isLoading: mappingLoading
  } = useFileMapping(filepath);

  // Fetch PDF blob on mount
  useEffect(() => {
    let mounted = true;

    const fetchPDF = async () => {
      try {
        setLoading(true);
        setError(null);

        // Use APIClient which handles auth automatically
        const response = await apiClient.files.downloadRaw(filepath);

        if (!response.ok) {
          throw new Error(`Failed to fetch PDF: ${response.statusText}`);
        }

        const blob = await response.blob();
        
        if (mounted) {
          const blobUrl = URL.createObjectURL(blob);
          setPdfBlobUrl(blobUrl);
        }
      } catch (err) {
        console.error('Failed to fetch PDF:', err);
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to load PDF');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    if (filepath) {
      fetchPDF();
    }

    return () => {
      mounted = false;
    };
  }, [filepath, apiClient]);

  // Separate cleanup effect for blob URL
  useEffect(() => {
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [pdfBlobUrl]);

  // Open PDF in new tab using the blob URL
  const viewPDFInNewTab = () => {
    if (pdfBlobUrl) {
      window.open(pdfBlobUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const downloadPDF = async () => {
    try {
      // Use APIClient which handles auth automatically
      const response = await apiClient.files.downloadRaw(filepath);

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  // Determine if file is processed
  const isProcessed = mappingData?.processed_files && mappingData.processed_files.length > 0;

  if (error) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader>
          <CardTitle className="text-lg truncate">{filename}</CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center text-red-600">
            <FileText className="w-12 h-12 mx-auto mb-4" />
            <p className="font-medium">Failed to load PDF</p>
            <p className="text-sm text-muted-foreground mt-1">{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex-shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg truncate">{filename}</CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant={isProcessed ? "default" : "secondary"}>
              {isProcessed ? "Processed" : "Pending"}
            </Badge>
            <Badge variant="outline">PDF</Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadPDF}
              disabled={!pdfBlobUrl}
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col min-h-0 p-2">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">Loading PDF...</p>
            </div>
          </div>
        ) : pdfBlobUrl ? (
          <div className="flex-1 bg-gray-50 rounded-md overflow-hidden">
            <object
              data={pdfBlobUrl}
              type="application/pdf"
              className="w-full h-full"
            >
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground mb-4">
                    Your browser doesn't support PDF viewing
                  </p>
                  <Button onClick={viewPDFInNewTab} variant="outline">
                    Open in New Tab
                  </Button>
                </div>
              </div>
            </object>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}